/**
 * ===== ADVANCED PERFORMANCE MONITOR - MONITORING AVANCÉ =====
 * Moniteur de performance avancé avec hit rate et optimisations
 */
import { logger } from './ProfessionalLogger';
import { KonvaCacheManager, CacheStats } from './KonvaCacheManager';

export interface PerformanceMetrics {
  fps: number;
  frameTime: number;
  memoryUsage: number;
  gpuUsage: number;
  cacheHitRate: number;
  renderTime: number;
  updateTime: number;
  drawTime: number;
  totalObjects: number;
  visibleObjects: number;
  performanceScore: number;
}

export interface PerformanceThresholds {
  minFPS: number;
  maxFrameTime: number;
  maxMemoryUsage: number;
  minCacheHitRate: number;
  maxRenderTime: number;
}

export interface PerformanceReport {
  timestamp: string;
  metrics: PerformanceMetrics;
  thresholds: PerformanceThresholds;
  issues: string[];
  recommendations: string[];
  score: number;
}

export class AdvancedPerformanceMonitor {
  private static instance: AdvancedPerformanceMonitor;
  private cacheManager: KonvaCacheManager;
  private metrics: PerformanceMetrics;
  private thresholds: PerformanceThresholds;
  private isMonitoring: boolean = false;
  private monitoringInterval: ReturnType<typeof setInterval> | null = null;
  private frameCount: number = 0;
  private lastFrameTime: number = 0;
  private performanceHistory: PerformanceMetrics[] = [];
  private maxHistorySize: number = 100;
  private lastLoggedIssues: string = '';
  private lastLogTime: number = 0;
  private logThrottleMs: number = 5000; // Loguer au maximum toutes les 5 secondes

  private constructor() {
    this.cacheManager = KonvaCacheManager.getInstance();
    this.metrics = {
      fps: 0,
      frameTime: 0,
      memoryUsage: 0,
      gpuUsage: 0,
      cacheHitRate: 0,
      renderTime: 0,
      updateTime: 0,
      drawTime: 0,
      totalObjects: 0,
      visibleObjects: 0,
      performanceScore: 0,
    };
    this.thresholds = {
      minFPS: 30,
      maxFrameTime: 33.33,
      maxMemoryUsage: 100 * 1024 * 1024,
      minCacheHitRate: 70,
      maxRenderTime: 16.67,
    };
  }

  public static getInstance(): AdvancedPerformanceMonitor {
    if (!AdvancedPerformanceMonitor.instance) {
      AdvancedPerformanceMonitor.instance = new AdvancedPerformanceMonitor();
    }
    return AdvancedPerformanceMonitor.instance;
  }

  /**
   * Initialise le monitoring
   */
  public initialize(): void {
    if (this.isMonitoring) {
      logger.debug('PerformanceMonitor déjà en cours');
      return;
    }
    this.startMonitoring();
    this.isMonitoring = true;
    logger.info('AdvancedPerformanceMonitor initialisé');
  }

  /**
   * Démarre le monitoring
   * NEUTRALISÉ: setInterval remplacé par hook React usePerformanceMonitor
   */
  private startMonitoring(): void {
    if (this.monitoringInterval) {
      clearInterval(this.monitoringInterval);
      this.monitoringInterval = null;
    }
    // NEUTRALISÉ: setInterval supprimé - géré par hook React
    // Le hook usePerformanceMonitor remplace cette fonctionnalité
    this.startFrameMonitoring();
  }

  /**
   * Démarre le monitoring des frames
   */
  private startFrameMonitoring(): void {
    const measureFrame = () => {
      const now = performance.now();
      if (this.lastFrameTime > 0) {
        const frameTime = now - this.lastFrameTime;
        this.frameCount++;
        if (this.frameCount % 10 === 0) {
          this.metrics.fps = 1000 / frameTime;
          this.metrics.frameTime = frameTime;
        }
      }
      this.lastFrameTime = now;
      requestAnimationFrame(measureFrame);
    };
    requestAnimationFrame(measureFrame);
  }

  /**
   * Met à jour les métriques
   * RENDU PUBLIC pour être utilisé par le hook React
   */
  public updateMetrics(): void {
    try {
      const cacheStats = this.cacheManager.getCacheStats();
      this.metrics.cacheHitRate = cacheStats.hitRate;
      this.metrics.memoryUsage = this.getMemoryUsage();
      this.metrics.gpuUsage = this.estimateGPUUsage();
      this.updateObjectMetrics();
      this.metrics.performanceScore = this.calculatePerformanceScore();
      this.addToHistory();
    } catch (error) {
      logger.error('Erreur lors de la mise à jour des métriques:', error);
    }
  }

  /**
   * Obtient l'utilisation mémoire
   */
  private getMemoryUsage(): number {
    // P3 : Chrome-only API. `performance.memory` n'est pas dans le lib.dom standard.
    if (typeof window === 'undefined') return 0;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const mem = (window.performance as any)?.memory;
    if (mem && typeof mem.usedJSHeapSize === 'number') {
      return mem.usedJSHeapSize as number;
    }
    return 0;
  }

  /**
   * Estime l'utilisation GPU
   */
  private estimateGPUUsage(): number {
    const objectComplexity = this.metrics.totalObjects * 0.1;
    const cacheComplexity = this.metrics.cacheHitRate < 50 ? 0.3 : 0.1;
    return Math.min(100, (objectComplexity + cacheComplexity) * 100);
  }

  /**
   * Met à jour les métriques d'objets
   */
  private updateObjectMetrics(): void {
    this.metrics.totalObjects = 0;
    this.metrics.visibleObjects = 0;
  }

  /**
   * Calcule le score de performance
   */
  private calculatePerformanceScore(): number {
    const fpsScore = Math.min(100, (this.metrics.fps / 60) * 100);
    const memoryScore = Math.max(
      0,
      100 - (this.metrics.memoryUsage / this.thresholds.maxMemoryUsage) * 100
    );
    const cacheScore = this.metrics.cacheHitRate;
    const frameTimeScore = Math.max(
      0,
      100 - (this.metrics.frameTime / this.thresholds.maxFrameTime) * 100
    );
    return (fpsScore + memoryScore + cacheScore + frameTimeScore) / 4;
  }

  /**
   * Ajoute les métriques à l'historique
   */
  private addToHistory(): void {
    this.performanceHistory.push({ ...this.metrics });
    if (this.performanceHistory.length > this.maxHistorySize) {
      this.performanceHistory.shift();
    }
  }

  /**
   * Analyse les performances
   * RENDU PUBLIC pour être utilisé par le hook React
   */
  public analyzePerformance(): void {
    const issues: string[] = [];
    const recommendations: string[] = [];

    if (this.metrics.fps < this.thresholds.minFPS) {
      issues.push(`FPS faible: ${this.metrics.fps.toFixed(1)} (min: ${this.thresholds.minFPS})`);
      recommendations.push("Réduire le nombre d'objets ou optimiser les animations");
    }

    if (this.metrics.frameTime > this.thresholds.maxFrameTime) {
      issues.push(
        `Temps de frame élevé: ${this.metrics.frameTime.toFixed(2)}ms (max: ${this.thresholds.maxFrameTime}ms)`
      );
      recommendations.push('Optimiser le rendu ou réduire la complexité des objets');
    }

    if (this.metrics.memoryUsage > this.thresholds.maxMemoryUsage) {
      issues.push(
        `Utilisation mémoire élevée: ${this.formatBytes(this.metrics.memoryUsage)} (max: ${this.formatBytes(this.thresholds.maxMemoryUsage)})`
      );
      recommendations.push("Nettoyer le cache ou réduire le nombre d'objets");
    }

    if (this.metrics.cacheHitRate < this.thresholds.minCacheHitRate) {
      issues.push(
        `Hit rate du cache faible: ${this.metrics.cacheHitRate.toFixed(1)}% (min: ${this.thresholds.minCacheHitRate}%)`
      );
      recommendations.push('Optimiser la stratégie de cache ou augmenter la taille du cache');
    }

    if (this.metrics.renderTime > this.thresholds.maxRenderTime) {
      issues.push(
        `Temps de rendu élevé: ${this.metrics.renderTime.toFixed(2)}ms (max: ${this.thresholds.maxRenderTime}ms)`
      );
      recommendations.push('Utiliser le cache Konva ou optimiser les objets');
    }

    if (issues.length > 0) {
      const issuesKey = issues.join('|');
      const now = Date.now();
      // Éviter les logs répétés - seulement si les problèmes ont changé ou après le throttle
      if (issuesKey !== this.lastLoggedIssues || now - this.lastLogTime > this.logThrottleMs) {
        logger.warn('Problèmes de performance détectés', { issues, recommendations });
        this.lastLoggedIssues = issuesKey;
        this.lastLogTime = now;
      }
    } else {
      // Réinitialiser si les problèmes sont résolus
      this.lastLoggedIssues = '';
    }
  }

  /**
   * Obtient les métriques actuelles
   */
  public getMetrics(): PerformanceMetrics {
    return { ...this.metrics };
  }

  /**
   * Obtient l'historique des performances
   */
  public getPerformanceHistory(): PerformanceMetrics[] {
    return [...this.performanceHistory];
  }

  /**
   * Génère un rapport de performance
   */
  public generateReport(): PerformanceReport {
    const issues: string[] = [];
    const recommendations: string[] = [];
    this.analyzePerformance();
    return {
      timestamp: new Date().toISOString(),
      metrics: { ...this.metrics },
      thresholds: { ...this.thresholds },
      issues,
      recommendations,
      score: this.metrics.performanceScore,
    };
  }

  /**
   * Optimise les performances
   */
  public optimizePerformance(): void {
    try {
      this.cacheManager.optimizeCache();
      this.cleanupMemory();
      this.optimizeRendering();
      logger.info('Optimisation des performances effectuée');
    } catch (error) {
      logger.error("Erreur lors de l'optimisation:", error);
    }
  }

  /**
   * Nettoie la mémoire
   */
  private cleanupMemory(): void {
    interface WindowWithGC extends Window {
      gc?: () => void;
    }
    if (typeof window !== 'undefined' && (window as WindowWithGC).gc) {
      (window as WindowWithGC).gc?.();
    }
    if (this.metrics.memoryUsage > this.thresholds.maxMemoryUsage) {
      this.cacheManager.performCleanup();
    }
  }

  /**
   * Optimise le rendu
   */
  private optimizeRendering(): void {
    logger.debug('Optimisation du rendu effectuée');
  }

  /**
   * Configure les seuils de performance
   */
  public configureThresholds(thresholds: Partial<PerformanceThresholds>): void {
    this.thresholds = { ...this.thresholds, ...thresholds };
    logger.info('Seuils de performance mis à jour', thresholds);
  }

  /**
   * Obtient les statistiques du cache
   */
  public getCacheStats(): CacheStats {
    return this.cacheManager.getCacheStats();
  }

  /**
   * Formate les bytes en format lisible
   */
  private formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  }

  /**
   * Arrête le monitoring
   */
  public stopMonitoring(): void {
    if (this.monitoringInterval) {
      clearInterval(this.monitoringInterval);
      this.monitoringInterval = null;
    }
    this.isMonitoring = false;
    logger.info('Monitoring des performances arrêté');
  }

  /**
   * Nettoie les ressources
   */
  public destroy(): void {
    this.stopMonitoring();
    this.performanceHistory = [];
    logger.info('AdvancedPerformanceMonitor détruit');
  }
}
